import * as React from "react";
import {
  FormControl,
  Link,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import {
  GraphqlClientSettings,
  RulesCommit,
  RunningEnvironment,
} from "./GraphqlSettingsPage";
import { KEY_GRAPHQL_SETTINGS } from "./common/KEY_GRAPHQL_SETTINGS";
import { useStateWithLocalStorage } from "./common/localStorage";
import Button from "@mui/material/Button";
import { useCallback } from "react";
import { getIdToken } from "@kahunmedical/user-management";
import { safeCopyToClipboard } from "fe-common/utils/safeCopyToClipboard";
import { downloadLargeObject } from "./util";
import {
  editorDevEndPoint,
  editorProdEndPoint,
  solverDevEndPoint,
  solverJDevLatestEndPoint,
  solverProdEndPoint,
} from "fe-common/graphql/endpoints";

//region [[ Styles ]]

const GraphQlEnvPickerView = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "space-between",
  flexWrap: "wrap",
});

const FromContainer = styled(FormControl)({
  marginTop: "25px",
  border: "none",
  flexBasis: "26%",
});

const SettingButton = styled(Button)({
  margin: "3px",
  flexBasis: "26%",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface GraphQlEnvPickerProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
function getEditorBackEndEndPoint(settings) {
  switch (settings.editorRunningEnv) {
    case RunningEnvironment.DEVELOP:
      return editorDevEndPoint;
    case RunningEnvironment.PRODUCTION:
      return editorProdEndPoint;
    case RunningEnvironment.CUSTOM:
      return settings.editorEndpoint;
  }
  return editorProdEndPoint;
}
//endregion [[ Functions ]]

async function downloadJavaModel(
  getIdTokenHeader: () => Promise<string>,
  settings,
  options?: string[],
) {
  const authorizationHeader = await getIdTokenHeader();
  let endPoint = `${getEditorBackEndEndPoint(settings)}/JsonExport/Export`;
  if (options?.length) endPoint += `?options=${options.join(",")}`;
  fetch(endPoint, {
    headers: {
      Authorization: authorizationHeader || "",
      "X-KAHUN-REPOSITORY": "kahun",
      "X-KAHUN-COMMITS-FILTER": settings.rules || "latest",
    },
  })
    .then((response) => {
      return response.blob();
    })
    .then((responseBlob) => {
      downloadLargeObject(responseBlob, "model.json");
    });
}

export const GraphQlEnvPicker = ({ ...props }: GraphQlEnvPickerProps) => {
  const [settings, setSettings] = useStateWithLocalStorage(
    KEY_GRAPHQL_SETTINGS,
  );

  const handleChange = (field: keyof GraphqlClientSettings) => (event: any) => {
    const v = event?.target?.value;
    if (v != undefined) setSettings((s) => ({ ...s, [field]: v }));
  };

  const getIdTokenHeader = useCallback(async () => {
    const idToken = await getIdToken();
    return `Bearer ${idToken}`;
  }, []);

  const onCopyToClipboardIdTokenHeader = useCallback(async () => {
    const authorizationHeader = await getIdTokenHeader();
    safeCopyToClipboard(authorizationHeader);
  }, []);

  const downloadModel = useCallback(async () => {
    await downloadJavaModel(getIdTokenHeader, settings);
  }, [settings]);

  const downloadGatewayModel = useCallback(async () => {
    await downloadJavaModel(getIdTokenHeader, settings, [
      "FEATURES",
      "RELATIONS",
      "POLICIES",
      "PROTOCOLS",
      "CITATIONS",
      "RULE_VALUES",
    ]);
  }, [settings]);

  return (
    <GraphQlEnvPickerView className={"graph-ql-env-picker"}>
      <Typography color="primary" variant="h4">
        Graphql Client Settings
      </Typography>
      <div>
        <SettingButton
          variant="contained"
          color="primary"
          onClick={onCopyToClipboardIdTokenHeader}
        >
          Copy Id Token to clipboard
        </SettingButton>
        <SettingButton
          variant="contained"
          color="primary"
          onClick={downloadModel}
        >
          Download model
        </SettingButton>
        <SettingButton
          variant="contained"
          color="primary"
          onClick={downloadGatewayModel}
        >
          Download lambda/gateway model
        </SettingButton>
      </div>
      <FromContainer>
        <FormLabel component="legend">Running Environment:</FormLabel>
        <RadioGroup
          name="env"
          value={settings.runningEnv}
          onChange={handleChange("runningEnv")}
        >
          <FormControlLabel
            value={RunningEnvironment.PRODUCTION}
            control={<Radio />}
            label="prod"
          />
          <FormControlLabel
            value={RunningEnvironment.DEVELOP}
            control={<Radio />}
            label="develop"
          />
          <FormControlLabel
            value={RunningEnvironment.JDEVLATEST}
            control={<Radio />}
            label="NEW SNOMED"
          />
          <FormControlLabel
            value={RunningEnvironment.CUSTOM}
            control={<Radio />}
            label="custom"
          />
        </RadioGroup>
      </FromContainer>
      <FromContainer>
        <FormLabel component="legend">Rules Commit:</FormLabel>
        <RadioGroup
          name="env"
          value={settings.rules}
          onChange={handleChange("rules")}
        >
          <FormControlLabel
            value={RulesCommit.latest}
            control={<Radio />}
            label="latest"
          />
          <FormControlLabel
            value={RulesCommit.moderated}
            control={<Radio />}
            label="moderated"
          />
        </RadioGroup>
      </FromContainer>
      <FromContainer>
        <TextField
          label="Preloader URL"
          placeholder={`http://localhost:4004`}
          value={settings.preloaderEndPoint}
          onChange={handleChange("preloaderEndPoint")}
        />
      </FromContainer>
      <FormControl component="fieldset" style={{ marginTop: 14 }}>
        <FormLabel component="legend">Endpoint Override:</FormLabel>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <TextField
            label="Custom Host URL"
            placeholder={`http://localhost:4000`}
            value={settings.endpoint || ""}
            onChange={handleChange("endpoint")}
            disabled={settings.runningEnv !== RunningEnvironment.CUSTOM}
            style={{ width: 240 }}
          />
          {settings.runningEnv === RunningEnvironment.CUSTOM && (
            <>
              {settings.endpoint && (
                <>
                  <Link
                    role="button"
                    onClick={() =>
                      setSettings((s) => ({
                        ...s,
                        endpoint: "",
                      }))
                    }
                  >
                    clear
                  </Link>
                  |
                </>
              )}
              <Link
                role="button"
                onClick={() =>
                  setSettings((s) => ({
                    ...s,
                    endpoint: "http://localhost:4000",
                  }))
                }
              >
                localhost
              </Link>
            </>
          )}
        </div>
      </FormControl>
    </GraphQlEnvPickerView>
  );
};
